import { useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * The green messages after an action (owner 2026-10-27 21:02: „te zielone komunikaty wyświetlaj zawsze na dole strony
 * (w każdym przypadku) i dołóż do każdego z nich krzyżyk do zamknięcia”; review: only the transient ones — „Zapisano”,
 * „Obsadzono…”, „Wydarzenie utworzone”, the quote bar — not the fixed notes, mode bars and warnings, which stay
 * `.notice` in their place). They render in ONE area pinned to the bottom of the window (`#notice-area`, stacked in
 * the order they came), each with its „×”.
 *
 * - A portal: the notice stays a part of its screen (its state, buttons and timers as before) — only where it shows
 *   changed. Inside a dialog (review I2) it stays in the dialog: its focus trap and backdrop are untouched.
 * - „×” only hides it and calls `onClose` (a screen that keeps the text drops it); `resetKey`: a notice closed by „×”
 *   shows again when its key changes. The focus then goes to the next notice's „×”, or back where it was (review M5).
 * - `dedupeKey` (review M1): one notice per key in the area — e.g. the quote bar of one event shown by two screens.
 * - A notice of the event card carries its keyboard zone (`data-key-zone="card"`, review I3, events/shortcuts.ts).
 * - Its height is `--notice-h` on the page: `body`, the card's scroll and the sticky bars make room for it (review I4).
 */
const AREA_ID = 'notice-area';
const DIALOG = '[role="dialog"], [role="alertdialog"], [aria-modal="true"]';

let observer: ResizeObserver | null = null;

function noticeArea(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const found = document.getElementById(AREA_ID);
  if (found) return found;
  const area = document.createElement('div');
  area.id = AREA_ID;
  area.className = 'notice-area';
  area.setAttribute('role', 'region');
  area.setAttribute('aria-label', 'Komunikaty');
  document.body.appendChild(area);
  if (typeof ResizeObserver !== 'undefined') {
    observer?.disconnect();
    observer = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--notice-h', `${Math.ceil(area.getBoundingClientRect().height)}px`);
    });
    observer.observe(area);
  }
  return area;
}

// the area is there from the start (empty: hidden), so a screen and its tests find it before the first notice
noticeArea();

/** review M1: the notices holding each `dedupeKey`, in the order they came; the first one shows. */
const holders = new Map<string, symbol[]>();
const listeners = new Set<() => void>();
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const changed = () => listeners.forEach((l) => l());

function useFirstOf(key: string | undefined): boolean {
  const [me] = useState(() => Symbol('notice'));
  useLayoutEffect(() => {
    if (!key) return;
    holders.set(key, [...(holders.get(key) ?? []), me]);
    changed();
    return () => {
      const rest = (holders.get(key) ?? []).filter((x) => x !== me);
      if (rest.length > 0) holders.set(key, rest);
      else holders.delete(key);
      changed();
    };
  }, [key, me]);
  return useSyncExternalStore(
    subscribe,
    () => !key || holders.get(key)?.[0] === me,
    () => true,
  );
}

const OPEN = Symbol('open');

export type NoticeProps = {
  children: ReactNode;
  /** Extra classes beside `notice` (the screen's own look of it). */
  className?: string;
  role?: 'status' | 'note' | 'alert';
  'aria-label'?: string;
  'data-feature-id'?: string;
  /** Called after „×” hid the notice. */
  onClose?: () => void;
  /** After „×”, the notice shows again once this changes. */
  resetKey?: unknown;
  /** One notice per key in the area (the first one); the others show when it is gone. */
  dedupeKey?: string;
};

type Place = { inline: boolean; zone: 'card' | null };

export function Notice({ children, className, role = 'status', onClose, resetKey, dedupeKey, ...rest }: NoticeProps) {
  const [closedAt, setClosedAt] = useState<unknown>(OPEN);
  const [place, setPlace] = useState<Place | null>(null);
  const anchor = useRef<HTMLSpanElement>(null);
  const before = useRef<Element | null>(null);
  const first = useFirstOf(dedupeKey);
  // where the screen put it: in a dialog → stays there; in the event card → the card's keyboard zone
  useLayoutEffect(() => {
    const a = anchor.current;
    if (!a) return;
    setPlace({ inline: !!a.closest(DIALOG), zone: a.closest('.cal-panel') ? 'card' : null });
  }, []);
  const closed = closedAt !== OPEN && Object.is(closedAt, resetKey);
  const close = (button: HTMLButtonElement) => {
    // review M5: the next notice's „×”, else where the focus was before
    const own = button.closest('.notice');
    const next = own?.nextElementSibling?.querySelector<HTMLElement>('.notice-close') ?? own?.previousElementSibling?.querySelector<HTMLElement>('.notice-close');
    const back = before.current instanceof HTMLElement && before.current.isConnected ? before.current : null;
    setClosedAt(resetKey);
    onClose?.();
    if (document.activeElement === button || document.activeElement === document.body) (next ?? back)?.focus();
  };
  const notice =
    place && !closed && first ? (
      <div className={className ? `notice ${className}` : 'notice'} role={role} {...(place.zone ? { 'data-key-zone': place.zone } : {})} {...rest}>
        <div className="notice-body">{children}</div>
        <button
          className="notice-close"
          type="button"
          aria-label="Zamknij komunikat"
          title="Zamknij"
          onPointerDown={() => {
            const now = document.activeElement;
            before.current = now && now !== document.body && !now.closest('.notice-area') ? now : before.current;
          }}
          onFocus={(e) => {
            const from = e.relatedTarget;
            if (from instanceof Element && !from.closest('.notice-area')) before.current = from;
          }}
          onClick={(e) => close(e.currentTarget)}
        />
      </div>
    ) : null;
  const area = place && !place.inline ? noticeArea() : null;
  return (
    <>
      <span ref={anchor} hidden data-notice-anchor="" />
      {notice && (area ? createPortal(notice, area) : notice)}
    </>
  );
}
